import { Box, Button, Chip } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReplayIcon from '@mui/icons-material/Replay';
import { useCallback, useReducer } from 'react';
import { toast } from 'react-toastify';
import EnhancedTable from '../../../enhancedTable/EnhancedTable';
import useDialogModal from '../../../../hooks/useDialogModal';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import validLookup from '../../../../utils/validLookup';
import { ValidDataService } from '../../../../service/validDataService';
import { DEFAULT_PAGE_SIZE } from '../../../../utils';
import { isLocalDev } from '../../../../utils/environment';

const columns = [
    {
        Header: 'Invoice #',
        accessor: 'invoiceNumber',
        id: 'invoiceNumber',
        enableFilter: true,
        type: 'text',
    },
    {
        Header: 'Description',
        accessor: 'description',
        id: 'description',
        enableFilter: true,
        type: 'text',
    },
    {
        Header: 'Frame Handling',
        accessor: 'frameHandling',
        id: 'frameHandling',
        enableFilter: true,
        type: 'enum',
        filterOptions: [
            { value: 'stock', label: 'Stock' },
            { value: 'order new', label: 'Order New' },
            { value: 'patient own', label: 'Patient Own' },
        ],
    },
    {
        Header: 'Model',
        accessor: 'modelNumber',
        id: 'modelNumber',
        enableFilter: true,
        type: 'text',
    },
    {
        Header: 'Location',
        accessor: 'locationName',
        id: 'locationName',
        enableFilter: true,
        type: 'text',
    },
    {
        Header: 'Qty',
        accessor: 'quantity',
        id: 'quantity',
        enableFilter: true,
        type: 'number',
    },
    {
        Header: 'Disposition',
        accessor: 'returnDisposition',
        id: 'returnDisposition',
        enableFilter: true,
        type: 'text',
    },
];

export default function DisposedFramesTab() {
    const [state, updateState] = useReducer((prev, next) => ({ ...prev, ...next }), {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
    });

    const [ConfirmationDialogComp, showConfirmationDialog, closeConfirmationDialog] = useDialogModal(ConfirmationDialog);

    const fetchData = useCallback(({ pageSize, pageIndex }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });

        ValidDataService.getDisposedFramesWithLimit(from, pageSize)
            .then((result) => {
                const { code, data } = result;
                if (code === 0) {
                    updateState({
                        data: data.data || [],
                        totalRowCount: data.count || 0,
                        pageCount: Math.ceil((data.count || 0) / pageSize),
                    });
                }
                updateState({ loading: false });
            })
            .catch((error) => {
                updateState({ loading: false });
                if (isLocalDev) {
                    console.error(error);
                }
                toast.error('Failed to load disposed frames');
            });
    }, []);

    const resolveRow = (rowData, action, message, tableState = {}) => {
        showConfirmationDialog(async (confirmAction) => {
            if (confirmAction === validLookup.confirmationDialogAction.ok) {
                try {
                    const response = await ValidDataService.resolveDisposedFrame(rowData.invoiceItemId, action);
                    const { code } = response;
                    if (code === 0) {
                        toast.success(message);
                        if (tableState.refreshCurrentPage) {
                            tableState.refreshCurrentPage();
                        } else {
                            fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 });
                        }
                    } else {
                        toast.error('Unable to update disposal status');
                    }
                } catch (error) {
                    if (isLocalDev) {
                        console.error(error);
                    }
                    toast.error('Unable to update disposal status');
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Confirm action',
            message,
        });
    };

    const actions = [
        {
            icon: <ReplayIcon />,
            onClick: (rowData, _rowIndex, tableState) => resolveRow(rowData, 'put_back_to_floor', `Put ${rowData.description || 'item'} back to floor?`, tableState),
            tooltip: 'Put Back to Floor',
        },
        {
            icon: <DeleteForeverIcon />,
            onClick: (rowData, _rowIndex, tableState) => resolveRow(rowData, 'throw_away', `Mark ${rowData.description || 'item'} as thrown away?`, tableState),
            tooltip: 'Throw Away',
        }
    ];

    return (
        <Box>
            <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip color='warning' size='small' label='Pending disposal decisions' />
                <Button size='small' variant='outlined' onClick={() => fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 })}>
                    Refresh
                </Button>
            </Box>

            <EnhancedTable
                columns={columns}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode='none'
                actions={actions}
            />

            <ConfirmationDialogComp
                label={{
                    positive: 'Confirm',
                    negative: 'Cancel',
                }}
            />
        </Box>
    );
}
